import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/toast-error";
import { AUTH_ME_QUERY_KEY, queryClient } from "@/lib/queryClient";
import { ME_QUERY_KEY } from "@/site/components/account/me-data";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

// Not dismissible: the 30-day window only protects a member who knows it is running (D5b).
export function DeletionBanner() {
  const { user } = useAuth();
  const scheduledAt = user?.scheduled_deletion_at ?? null;

  // Same route the Close Account page uses; it can't go through Eden (the `delete` segment
  // collides with the DELETE accessor on /user/me).
  const keep = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/user/me/delete/cancel", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Couldn't cancel the deletion. Try again.");
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
      ]);
      toast({ variant: "info", description: "Your account will not be deleted." });
    },
    onError: (e) => toastApiError(e, "Couldn't cancel the deletion. Try again."),
  });

  if (!scheduledAt) return null;
  const date = new Date(scheduledAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-3 bg-error-main px-4 py-1.5 text-sm font-medium text-white"
      data-testid="deletion-banner"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="truncate">
        Your account will be deleted on <strong>{date}</strong>.
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-6 border-white/60 bg-transparent px-2 text-xs text-white hover:bg-white/15"
        disabled={keep.isPending}
        onClick={() => keep.mutate()}
        data-testid="button-keep-account"
      >
        {keep.isPending ? "Keeping…" : "Keep my account"}
      </Button>
    </div>
  );
}
